import type {
  ReviewListResponse,
  NewReviewBody,
  Review,
  QuestionListResponse,
  Question,
  NewQuestionBody,
  NewQuestionReplyBody,
  AddQuestionReplyResponse,
} from '~/types/Feedback';
import type { Pagination } from '~/types/Pagination';

interface FeedbackStore {
  reviews: Review[];
  reviewsPagination: Pagination | null;
  averageRating: string;
  isReviewsLoaded: boolean;
  questions: Question[];
  questionsPagination: Pagination | null;
  isQuestionsLoaded: boolean;
}

export const useFeedbackStore = createFeedbackStore({
  reviews: [],
  reviewsPagination: null,
  averageRating: '0.0',
  isReviewsLoaded: false,
  questions: [],
  questionsPagination: null,
  isQuestionsLoaded: false,
});

export function createFeedbackStore(initialState: FeedbackStore) {
  return defineStore('feedback', {
    state: () => initialState,

    actions: {
      async getReviews(productId: number) {
        try {
          this.isReviewsLoaded = false;

          const { data } = await useCustomFetch<ReviewListResponse>(`/testimonials/${productId}`, {}, '2', true);

          if (data.value) {
            this.reviews = data.value.testimonials;
            this.reviewsPagination = data.value.pagination;
            this.averageRating = data.value.rating;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        } finally {
          this.isReviewsLoaded = true;
        }
      },

      async loadMoreReviews(productId: number, page: number) {
        const data = await useCustomFetchClient<ReviewListResponse>(
          `/testimonials/${productId}`,
          { query: { page } },
          '2',
          true,
        );

        if (data) {
          this.reviews = [...this.reviews, ...data.testimonials];
          this.reviewsPagination = data.pagination;
        }
      },

      async sendReview(review: NewReviewBody) {
        const form = new FormData();

        for (const [key, value] of Object.entries(review)) {
          if (key === 'images') {
            for (let i = 0; i < review.images.length; i++) {
              form.append('images[]', review.images[i]);
            }
          } else {
            form.append(key, value);
          }
        }

        const data = await useCustomFetchClient<{ data: any[]; success: boolean }>('/testimonials', {
          method: 'POST',
          body: form,
        });

        return data?.success;
      },

      async getQuestions(productId: number) {
        try {
          this.isQuestionsLoaded = false;

          const { data } = await useCustomFetch<QuestionListResponse>(`/questions/${productId}`, {}, '2', true);

          if (data.value) {
            this.questions = data.value.questions;
            this.questionsPagination = data.value.pagination;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        } finally {
          this.isQuestionsLoaded = true;
        }
      },

      async loadMoreQuestions(productId: number, page: number) {
        const data = await useCustomFetchClient<QuestionListResponse>(
          `/questions/${productId}`,
          { query: { page } },
          '2',
          true,
        );

        if (data) {
          this.questionsPagination = data.pagination;
          this.questions = [...this.questions, ...data.questions];
        }
      },

      async sendQuestion(question: NewQuestionBody) {
        const form = new FormData();

        for (const [key, value] of Object.entries(question)) {
          if (key === 'images') {
            for (let i = 0; i < question.images.length; i++) {
              form.append('images[]', question.images[i]);
            }
          } else {
            form.append(key, value);
          }
        }

        const data = await useCustomFetchClient<{ data: any[]; success: boolean }>(
          '/questions',
          { method: 'POST', body: form },
          '2',
        );

        return data?.success;
      },

      async likeQuestion(questionId: number) {
        const data = await useCustomFetchClient<{ message: string; rating: number[]; rating_count: number }>(
          `/questions/${questionId}/like`,
          { method: 'POST' },
          '2',
        );

        if (data?.rating)
          this.questions = this.questions.map((question) =>
            question.id === questionId ? { ...question, rating: data.rating } : question,
          );
      },

      async sendQuestionReply(questionId: number, reply: NewQuestionReplyBody) {
        const form = new FormData();

        for (const [key, value] of Object.entries(reply)) {
          if (key === 'images') {
            for (let i = 0; i < reply.images.length; i++) {
              form.append('images[]', reply.images[i]);
            }
          } else {
            form.append(key, value);
          }
        }

        const data = await useCustomFetchClient<AddQuestionReplyResponse>(
          `/questions/${questionId}/replies`,
          { method: 'POST', body: form },
          '2',
        );

        return data ?? null;
      },

      async likeQuestionReply(replyQuestionId: number) {
        const data = await useCustomFetchClient<{
          message: string;
          likers: number[];
          dislikers: number[];
          likes_count: number;
          dislikes_count: number;
        }>(`/questions/replies/${replyQuestionId}/like`, { method: 'POST' }, '2');

        return data?.likers ?? [];
      },

      async dislikeQuestionReply(replyQuestionId: number) {
        const data = await useCustomFetchClient<{
          message: string;
          likers: number[];
          dislikers: number[];
          likes_count: number;
          dislikes_count: number;
        }>(`/questions/replies/${replyQuestionId}/dislike`, { method: 'POST' }, '2');

        return data?.dislikers ?? [];
      },
    },
  });
}
